import { ShowOn } from '../../../../types/helios/Question';
import { isSubVisible } from './deriveParentResult';

/**
 * Shared, generic empty-array reference used when the caller passes
 * `undefined`. Hoisted to module scope so callers who put the result
 * into a `useMemo` dep list or a memoized child prop never see a
 * fresh `[]` each render for the null-input case. The `readonly`
 * annotation is the workspace's canonical immutability shape —
 * `Object.freeze` is banned by `no-restricted-syntax` since runtime
 * freezing adds no value over the compile-time guarantee.
 */
const EMPTY: readonly unknown[] = [];

/**
 * Structural minimum a sub-question needs to expose so the visibility
 * filter can decide. Matches the field carried by both the SDK
 * `SubQuestion` (flyout raw defs) and the render layer's
 * `DisplayableSubQuestion` (perform-audit answer view).
 */
export interface SubQuestionVisibilityRef {
  readonly showWhenParentIs?: string | ShowOn | undefined;
}

/**
 * Returns the sub-questions that should be VISIBLE to the auditor
 * given the parent question's current committed result.
 *
 * Two-layer rule:
 *   - Non-conditional parent (`hasConditionalSubQuestions === false`
 *     or `undefined`) → every sub passes through, reference-preserved.
 *   - Conditional parent → each sub runs through {@link isSubVisible}
 *     which checks `showWhenParentIs` against `parentResult` (PASS,
 *     FAIL, BOTH, or hidden entirely when the parent is unanswered
 *     or NA).
 *
 * The reference-preservation branch matters: the render layer, the
 * completion counter, and the flyout each consume this array
 * directly. Allocating a fresh copy for every non-conditional call
 * would break memoization on downstream `useMemo` / effect dep
 * arrays that key off the sub array identity. The filter branch has
 * to allocate — `.filter()` always does — but that only fires for
 * conditional parents, which are a small fraction of audit questions.
 *
 * Generic over `T` so callers keep their specific input type
 * (`DisplayableSubQuestion`, `SubQuestion`, whatever) in the result
 * — no downstream cast needed.
 *
 * Pure. No react, no side effects. Used by:
 *   - `PerformAuditQuestionInteraction` (answer view)
 *   - `QuestionNavItem` in `PerformAuditQuestionNavRow` (flyout)
 *   - `isQuestionCompleted` in `performAuditHelpers` (completion metrics)
 *
 * See {@link isSubVisible} for the underlying primitive.
 */
export function resolveVisibleSubQuestions<T extends SubQuestionVisibilityRef>(
  subQuestions: readonly T[] | undefined,
  hasConditionalSubQuestions: boolean,
  parentResult: string | undefined,
): readonly T[] {
  if (!subQuestions) return EMPTY as readonly T[];
  if (!hasConditionalSubQuestions) return subQuestions;
  return subQuestions.filter((sq) => isSubVisible(sq, parentResult));
}
